import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Sync stored user language preference with i18n
      if (response.data.language && response.data.language !== i18n.language) {
        i18n.changeLanguage(response.data.language);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Sync stored user language preference with i18n
    if (userData.language && userData.language !== i18n.language) {
      i18n.changeLanguage(userData.language);
    }
    return userData;
  };

  const register = async (email, password, name, role = 'user', group_id = null, site_id = null) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name, 
      role,
      group_id,
      site_id,
      language: localStorage.getItem('i18nextLng') || 'en'
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateLanguage = async (language) => {
    if (token) {
      try {
        await axios.put(`${API}/auth/language`, { language });
        setUser(prev => ({ ...prev, language }));
      } catch (error) {
        console.error('Failed to update language:', error);
      }
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateLanguage,
      refreshUser,
      isAdmin: user?.role === 'admin',
      isGroupAdmin: user?.role === 'group_admin',
      isAnyAdmin: user?.role === 'admin' || user?.role === 'group_admin',
      isSiteManager: user?.role === 'site_manager' || user?.role === 'admin' || user?.role === 'group_admin',
      isExternal: user?.role === 'external',
      isApproved: user?.approved === true
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
